import type {KeyboardEvent} from "react";
import {Autocomplete, CircularProgress} from "@mui/material";
import {Box, Colors, Text} from "@bounce/shared/ui";
import {Button, TextField} from "../../../ui/components";
import {ReachAccountSearchResult} from "../../../apis/requests/accounts/searchAccounts";
import {ReachTemplateLanguage} from "../../../apis/requests/templates/types";

const MIN_SEARCH_LENGTH = 3;

const LANGUAGE_OPTIONS: ReachTemplateLanguage[] = ["en", "es"];

const formatAccountLabel = (a: ReachAccountSearchResult) =>
  `${a.first_name} ${a.last_name} — ${a.account_public_id}`;

type RenderAsUserSectionProps = {
  selected: ReachAccountSearchResult | null;
  searchInput: string;
  onSearchInputChange: (v: string) => void;
  onSearch: () => void;
  onSelect: (account: ReachAccountSearchResult | null) => void;
  options: ReachAccountSearchResult[];
  isSearching: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showLanguageToggle?: boolean;
  language?: ReachTemplateLanguage;
  onLanguageChange?: (language: ReachTemplateLanguage) => void;
};

const RenderAsUserSection = ({
  selected,
  searchInput,
  onSearchInputChange,
  onSearch,
  onSelect,
  options,
  isSearching,
  open,
  onOpenChange,
  showLanguageToggle,
  language,
  onLanguageChange,
}: RenderAsUserSectionProps) => {
  const canSearch = searchInput.trim().length >= MIN_SEARCH_LENGTH;

  return (
  <Box sx={{display: "flex", flexDirection: "column", gap: 0.75}}>
    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
      <Text size={13} sx={{color: Colors.grey._700}}>
        Render as user
      </Text>
      {showLanguageToggle && (
        <Box sx={{display: "flex", gap: 0.5, backgroundColor: Colors.tertiary._100, borderRadius: "6px", p: "2px"}}>
          {LANGUAGE_OPTIONS.map((option) => (
            <Box
              key={option}
              onClick={() => onLanguageChange?.(option)}
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: "5px",
                cursor: "pointer",
                userSelect: "none",
                backgroundColor: language === option ? Colors.bw.white : "transparent",
                boxShadow: language === option ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
              }}
            >
              <Text
                size={12}
                variant="inter600"
                sx={{color: language === option ? Colors.grey._900 : Colors.grey._500}}
              >
                {option.toUpperCase()}
              </Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
    <Box sx={{display: "flex", gap: 1, alignItems: "flex-start"}}>
      <Autocomplete<ReachAccountSearchResult, false, false, false>
        size="small"
        selectOnFocus
        forcePopupIcon={false}
        open={open}
        onClose={() => onOpenChange(false)}
        value={selected}
        inputValue={selected ? formatAccountLabel(selected) : searchInput}
        onChange={(_, value) => onSelect(value)}
        onInputChange={(_, value, reason) => {
          if (reason === "input") {
            if (selected) onSelect(null);
            onSearchInputChange(value);
            onOpenChange(false);
          }
          if (reason === "clear") {
            onSearchInputChange("");
            onSelect(null);
            onOpenChange(false);
          }
        }}
        options={options}
        filterOptions={(x) => x}
        getOptionLabel={formatAccountLabel}
        isOptionEqualToValue={(opt, val) => opt.account_id === val.account_id}
        loading={isSearching}
        noOptionsText={
          canSearch ? "No accounts found" : `Type at least ${MIN_SEARCH_LENGTH} characters, then search`
        }
        sx={{flex: 1}}
        renderInput={(params) => {
          const muiOnKeyDown = params.inputProps.onKeyDown;
          return (
            <TextField
              {...params}
              placeholder="Search by name or BA-…"
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter" && !open) {
                      e.preventDefault();
                      onSearch();
                      return;
                    }
                    muiOnKeyDown?.(e);
                  },
                },
              }}
            />
          );
        }}
      />
      <Button
        buttonType="outlined-primary"
        size="small"
        onClick={onSearch}
        disabled={!canSearch || isSearching}
        sx={{minWidth: 88, height: 40}}
      >
        {isSearching ? <CircularProgress size={14} sx={{color: Colors.blue._700}} /> : "Search"}
      </Button>
    </Box>
  </Box>
  );
};

export default RenderAsUserSection;
export {MIN_SEARCH_LENGTH};
